import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

type Disclosure = {
  id: string;
  title: string;
  summary?: string;
  bodyHtml?: string;
  writtenDate?: string;
  authorName?: string;
  type?: string;
  lastEditedAtUtc?: string;
  supportingLinks?: string[];
  readCount?: number;
  upvotes?: number;
  downvotes?: number;
  voteScore?: number;
  discussionCount?: number;
  hasAudio?: boolean;
};

type VoteResponse = {
  ok?: boolean;
  alreadyVoted?: boolean;
  upvotes?: number;
  downvotes?: number;
};

type ReadResponse = {
  ok?: boolean;
  alreadyRead?: boolean;
  readCount?: number;
};

const API_BASE = (typeof window !== 'undefined' && (window as any).UNBOUGHTPRESS_API_BASE) || 'https://api.unboughtpress.com';
const READ_DELAY_MS = 5 * 60 * 1000;
const FUTURE_SKEW_MS = 5 * 60 * 1000; // tolerate small drift; hide obviously future timestamps
const SITE_BASE_URL = (typeof window !== 'undefined' && window.location?.origin) || 'https://unboughtpress.com';
const DEFAULT_SOCIAL_IMAGE_URL = `${SITE_BASE_URL}/images/og-unboughtpress-1200x630.png`;
const MAIN_LOGO_IMAGE_URL = 'images/signal-u-transparent.png';

function normalizeSummaryText(value?: string | null, fallback = ''): string {
  const normalized = (value ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return normalized || fallback;
}

function upsertMetaTag(name: string | null, property: string | null, content: string) {
  const selector = name ? `meta[name="${name}"]` : property ? `meta[property="${property}"]` : null;
  if (!selector) return;
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    if (name) {
      element.name = name;
    }
    if (property) {
      element.setAttribute('property', property);
    }
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonicalUrl(url: string) {
  let canonicalLink = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.href = url;
}

function upsertJsonLd(id: string, payload: object) {
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(payload);
}

function scoreValue(upvotes = 0, downvotes = 0) {
  const raw = upvotes - downvotes;
  if (raw >= 0) return raw;
  if (raw <= -10) return -99;
  return 0;
}

function ensureClientGuid(): string {
  if (typeof window === 'undefined') return 'anon';
  const fromStorage = window.localStorage.getItem('up_client_guid');
  const fromCookie = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith('up_client_guid='))
    ?.split('=')[1];
  const guid = fromStorage || fromCookie || (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
  window.localStorage.setItem('up_client_guid', guid);
  document.cookie = `up_client_guid=${guid}; path=/; max-age=31536000; SameSite=None; Secure`;
  return guid;
}

function clientHeaders(): Record<string, string> {
  const guid = ensureClientGuid();
  return guid ? { 'X-Client-Guid': guid } : {};
}

async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: clientHeaders(),
    credentials: 'include'
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Failed request: ${res.status}`);
  }
  return res.json();
}

async function apiPost<T>(path: string, body: Record<string, any> = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...clientHeaders() },
    credentials: 'include',
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Failed request: ${res.status}`);
  }
  return res.json().catch(() => ({} as T));
}

function formatDate(iso?: string | null) {
  if (!iso) return '';
  const date = new Date(iso);
  const now = Date.now();
  const maxAllowed = now + FUTURE_SKEW_MS;
  const clampedTime = Math.min(date.getTime(), maxAllowed);
  if (Number.isNaN(clampedTime)) return '';
  const safeDate = new Date(clampedTime);
  return safeDate.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  });
}

function getQueryParam(key: string) {
  const params = new URLSearchParams(window.location.search);
  return params.get(key);
}

function formatTimer(seconds: number) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

function ensureImageZoomOverlay() {
  if (typeof document === 'undefined') return { overlay: null, zoomImg: null };
  let overlay = document.getElementById('image-zoom-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'image-zoom-overlay';
    overlay.className = 'image-zoom-overlay';
    overlay.innerHTML = '<img alt="Zoomed image" />';
    document.body.appendChild(overlay);
  }
  const zoomImg = overlay.querySelector('img') as HTMLImageElement | null;
  if (!overlay.dataset.bound) {
    const hide = () => {
      overlay?.classList.remove('active');
      if (zoomImg) zoomImg.src = '';
    };
    overlay.addEventListener('click', hide);
    overlay.addEventListener('mouseleave', hide);
    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape') hide();
    });
    overlay.dataset.bound = 'true';
  }
  return { overlay, zoomImg };
}

function getAudioDuration(blob: Blob): Promise<number | null> {
  return new Promise((resolve) => {
    const audioEl = document.createElement('audio');
    audioEl.preload = 'metadata';
    audioEl.onloadedmetadata = () => {
      resolve(audioEl.duration || null);
      URL.revokeObjectURL(audioEl.src);
    };
    audioEl.onerror = () => resolve(null);
    audioEl.src = URL.createObjectURL(blob);
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result);
    };
    reader.onerror = () => reject(reader.error || new Error('Unable to read blob'));
    reader.readAsDataURL(blob);
  });
}

function useReadTimer(disclosureId: string | null, onConfirm: () => void, onTrigger: () => Promise<void>) {
  const elapsedRef = useRef(0);
  const triggeredRef = useRef(false);
  const confirmedRef = useRef(false);
  const timerIdRef = useRef<number | null>(null);
  const lastVisibleRef = useRef<number | null>(document.visibilityState === 'visible' ? Date.now() : null);
  const confirmRef = useRef(onConfirm);
  const triggerRef = useRef(onTrigger);

  useEffect(() => {
    confirmRef.current = onConfirm;
    triggerRef.current = onTrigger;
  }, [onConfirm, onTrigger]);

  useEffect(() => {
    if (!disclosureId) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (lastVisibleRef.current) {
          elapsedRef.current += Date.now() - lastVisibleRef.current;
          lastVisibleRef.current = null;
        }
      } else {
        lastVisibleRef.current = Date.now();
      }
    };

    const tryTrigger = async () => {
      if (triggeredRef.current) return;
      const now = Date.now();
      if (lastVisibleRef.current) {
        elapsedRef.current += now - lastVisibleRef.current;
        lastVisibleRef.current = now;
      }
      if (elapsedRef.current >= READ_DELAY_MS) {
        triggeredRef.current = true;
        try {
          await triggerRef.current();
          confirmedRef.current = true;
          confirmRef.current();
        } catch (err) {
          console.error(err);
        }
      }
    };

    timerIdRef.current = window.setInterval(tryTrigger, 1000);
    document.addEventListener('visibilitychange', handleVisibility);

    const sendBeaconIfNeeded = () => {
      const now = Date.now();
      const total = elapsedRef.current + (lastVisibleRef.current ? now - lastVisibleRef.current : 0);
      if (total >= READ_DELAY_MS && !confirmedRef.current) {
        const url = `${API_BASE}/disclosures/${encodeURIComponent(disclosureId)}/read`;
        const headers = { ...clientHeaders(), 'Content-Type': 'application/json' };
        const payload = new Blob([JSON.stringify({})], { type: 'application/json' });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, payload);
        } else {
          fetch(url, { method: 'POST', body: payload, headers, credentials: 'include', keepalive: true }).catch(() => {});
        }
      }
    };

    window.addEventListener('pagehide', sendBeaconIfNeeded);
    window.addEventListener('beforeunload', sendBeaconIfNeeded);

    return () => {
      if (timerIdRef.current) window.clearInterval(timerIdRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', sendBeaconIfNeeded);
      window.removeEventListener('beforeunload', sendBeaconIfNeeded);
    };
  }, [disclosureId]);
}

function typeClass(type?: string) {
  const key = (type || 'Disclosure').toLowerCase();
  if (key.includes('investigation')) return 'pill-investigation';
  if (key.includes('explainer')) return 'pill-explainer';
  if (key.includes('voice')) return 'pill-voice';
  return 'pill-generic';
}

function applyDisclosureSeo(disclosure: Disclosure) {
  if (typeof document === 'undefined') return;
  const pageUrl = `${SITE_BASE_URL}/disclosure.html?id=${encodeURIComponent(disclosure.id)}`;
  const pageTitle = disclosure.title ? `${disclosure.title} | Unbought Press` : 'Disclosure | Unbought Press';
  const description = normalizeSummaryText(disclosure.summary ?? disclosure.bodyHtml, 'Unbought Press disclosure backed by evidence and receipts.');
  const authorEntity =
    disclosure.authorName?.trim().length > 0
      ? { '@type': 'Person', name: disclosure.authorName }
      : { '@id': 'https://unboughtpress.com/#organization' };
  const keywords = [disclosure.type].filter(Boolean);

  document.title = pageTitle;
  setCanonicalUrl(pageUrl);
  upsertMetaTag('description', null, description);
  upsertMetaTag(null, 'og:site_name', 'Unbought Press');
  upsertMetaTag(null, 'og:type', 'article');
  upsertMetaTag(null, 'og:title', pageTitle);
  upsertMetaTag(null, 'og:description', description);
  upsertMetaTag(null, 'og:url', pageUrl);
  upsertMetaTag(null, 'og:image', DEFAULT_SOCIAL_IMAGE_URL);
  upsertMetaTag('twitter:card', null, 'summary_large_image');
  upsertMetaTag('twitter:title', null, pageTitle);
  upsertMetaTag('twitter:description', null, description);
  upsertMetaTag('twitter:image', null, DEFAULT_SOCIAL_IMAGE_URL);

  const structuredData: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: disclosure.title ?? 'Unbought Press Disclosure',
    description,
    mainEntityOfPage: pageUrl,
    url: pageUrl,
    publisher: { '@id': 'https://unboughtpress.com/#organization' },
    image: DEFAULT_SOCIAL_IMAGE_URL,
    author: authorEntity
  };
  if (keywords.length) {
    structuredData.keywords = keywords;
  }
  if (disclosure.writtenDate) {
    structuredData.datePublished = disclosure.writtenDate;
  }
  if (disclosure.lastEditedAtUtc) {
    structuredData.dateModified = disclosure.lastEditedAtUtc;
  }

  upsertJsonLd('ld-disclosure', structuredData);
}

const DisclosureApp: React.FC<{ disclosureId: string }> = ({ disclosureId }) => {
  const [disclosure, setDisclosure] = useState<Disclosure | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [voteStatus, setVoteStatus] = useState<string>('');
  const [voteLoading, setVoteLoading] = useState<1 | -1 | null>(null);
  const [readStatus, setReadStatus] = useState<'pending' | 'triggered' | 'confirmed'>('pending');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [recordModalOpen, setRecordModalOpen] = useState(false);
  const [recordingState, setRecordingState] = useState<'idle' | 'recording' | 'paused' | 'stopped'>('idle');
  const [recordingStatus, setRecordingStatus] = useState('');
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordPreviewUrl, setRecordPreviewUrl] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  const recordTimerRef = useRef<number | null>(null);
  const recordStartRef = useRef<number | null>(null);
  const listenRequested = useMemo(() => getQueryParam('listen') === '1', []);
  const MAX_RECORD_SECONDS = 180;
  const MAX_RECORD_BYTES = 3 * 1024 * 1024;

  useEffect(() => {
    ensureClientGuid();
    setLoading(true);
    apiGet<Disclosure>(`/disclosures/${encodeURIComponent(disclosureId)}`)
      .then((data) => {
        setDisclosure(data);
        setError(null);
      })
      .catch((err) => {
        console.error(err);
        setError('Unable to load this disclosure right now.');
      })
      .finally(() => setLoading(false));
  }, [disclosureId]);

  useEffect(() => {
    if (disclosure) {
      applyDisclosureSeo(disclosure);
    }
  }, [disclosure]);

  const voteScore = useMemo(() => scoreValue(disclosure?.upvotes ?? 0, disclosure?.downvotes ?? 0), [disclosure]);

  const handleVote = async (value: 1 | -1) => {
    if (!disclosure) return;
    setVoteLoading(value);
    setVoteStatus('');
    try {
      const endpoint = value === 1 ? 'agree' : 'disagree';
      const result = await apiPost<VoteResponse>(`/disclosures/${encodeURIComponent(disclosureId)}/${endpoint}`, {});
      setDisclosure((prev) =>
        prev
          ? {
              ...prev,
              upvotes: typeof result.upvotes === 'number' ? result.upvotes : prev.upvotes,
              downvotes: typeof result.downvotes === 'number' ? result.downvotes : prev.downvotes
            }
          : prev
      );
      if (result.alreadyVoted) {
        setVoteStatus('Vote already recorded for this device.');
      } else {
        setVoteStatus('Vote recorded.');
      }
    } catch (err) {
      console.error(err);
      setVoteStatus('Unable to record vote. Please try again.');
    } finally {
      setVoteLoading(null);
    }
  };

  const triggerRead = async () => {
    if (readStatus !== 'pending') return;
    setReadStatus('triggered');
    const result = await apiPost<ReadResponse>(`/disclosures/${encodeURIComponent(disclosureId)}/read`, {});
    setDisclosure((prev) =>
      prev ? { ...prev, readCount: typeof result.readCount === 'number' ? result.readCount : prev.readCount } : prev
    );
    setReadStatus('confirmed');
  };

  useReadTimer(
    disclosure ? disclosure.id : null,
    () => setReadStatus('confirmed'),
    async () => {
      try {
        await triggerRead();
      } catch (err) {
        console.error(err);
      }
    }
  );

  useEffect(() => {
    if (disclosure?.hasAudio && listenRequested && audioRef.current) {
      const play = () => {
        audioRef.current?.play().catch(() => {
          // autoplay might be blocked; ignore
        });
      };
      const timer = window.setTimeout(play, 300);
      return () => window.clearTimeout(timer);
    }
  }, [disclosure?.id, disclosure?.hasAudio, listenRequested]);

  useEffect(() => {
    return () => {
      if (recordTimerRef.current) window.clearInterval(recordTimerRef.current);
      mediaRecorderRef.current?.stream.getTracks().forEach((t) => t.stop());
      if (recordPreviewUrl) URL.revokeObjectURL(recordPreviewUrl);
    };
  }, [recordPreviewUrl]);

  const clearRecordTimer = () => {
    if (recordTimerRef.current) {
      window.clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
  };

  const resetRecording = () => {
    clearRecordTimer();
    recordChunksRef.current = [];
    recordStartRef.current = null;
    setRecordSeconds(0);
    setRecordingState('idle');
    setRecordingStatus('');
    setRecordedBlob(null);
    if (recordPreviewUrl) {
      URL.revokeObjectURL(recordPreviewUrl);
      setRecordPreviewUrl(null);
    }
  };

  const updateTimer = () => {
    if (!recordStartRef.current) return;
    const elapsed = Math.floor((Date.now() - recordStartRef.current) / 1000);
    setRecordSeconds(elapsed);
    if (elapsed >= MAX_RECORD_SECONDS) {
      stopRecording();
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setRecordingStatus('Recording not supported in this browser.');
      return;
    }
    try {
      mediaRecorderRef.current?.stream.getTracks().forEach((t) => t.stop());
    } catch {
      // ignore
    }
    if (recordPreviewUrl) {
      URL.revokeObjectURL(recordPreviewUrl);
      setRecordPreviewUrl(null);
    }
    setRecordedBlob(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, sampleRate: 16000 } });
      recordChunksRef.current = [];
      recordStartRef.current = Date.now();
      setRecordSeconds(0);
      setRecordingStatus('Recording...');
      setRecordingState('recording');
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus', audioBitsPerSecond: 24000 });
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (evt) => {
        if (evt.data && evt.data.size > 0) recordChunksRef.current.push(evt.data);
      };
      recorder.onstop = () => {
        clearRecordTimer();
        const blob = recordChunksRef.current.length ? new Blob(recordChunksRef.current, { type: 'audio/webm' }) : null;
        if (blob) {
          if (blob.size > MAX_RECORD_BYTES) {
            setRecordingStatus('Audio too large (max 3 MB). Please re-record.');
            setRecordedBlob(null);
          } else {
            setRecordedBlob(blob);
            if (recordPreviewUrl) URL.revokeObjectURL(recordPreviewUrl);
            const preview = URL.createObjectURL(blob);
            setRecordPreviewUrl(preview);
            setRecordingStatus('Recording ready. Save to continue.');
          }
        } else {
          setRecordingStatus('No audio captured. Try again.');
          setRecordedBlob(null);
        }
        setRecordingState('stopped');
      };
      recorder.start();
      clearRecordTimer();
      recordTimerRef.current = window.setInterval(updateTimer, 500);
    } catch (err) {
      console.error(err);
      setRecordingStatus('Unable to start recording. Check microphone permissions.');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setRecordingState('paused');
      setRecordingStatus('Paused');
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setRecordingState('recording');
      setRecordingStatus('Recording...');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && (mediaRecorderRef.current.state === 'recording' || mediaRecorderRef.current.state === 'paused')) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop());
    }
  };

  const saveRecording = async () => {
    if (recordingState === 'recording' || recordingState === 'paused') {
      setRecordingStatus('Stop recording before saving.');
      return;
    }
    if (!recordedBlob || !disclosure) {
      setRecordingStatus('Record audio first.');
      return;
    }
    const duration = await getAudioDuration(recordedBlob);
    if (duration && duration > MAX_RECORD_SECONDS + 1) {
      setRecordingStatus('Audio too long (max 3 minutes).');
      return;
    }
    if (recordedBlob.size > MAX_RECORD_BYTES) {
      setRecordingStatus('Audio too large (max 3 MB).');
      return;
    }
    setRecordingStatus('Preparing audio...');
    try {
      const dataUrl = await blobToDataUrl(recordedBlob);
      const key = `up_pending_discussion_audio_${disclosure.id}`;
      const payload = {
        dataUrl,
        mimeType: recordedBlob.type || 'audio/webm',
        durationSeconds: duration || null,
        createdAtUtc: new Date().toISOString()
      };
      sessionStorage.setItem(key, JSON.stringify(payload));
      window.location.href = `submit-discussion.html?id=${encodeURIComponent(disclosure.id)}`;
    } catch (err) {
      console.error(err);
      setRecordingStatus('Unable to save audio. Try again.');
    }
  };

  const openRecordModal = () => {
    resetRecording();
    setRecordModalOpen(true);
  };

  const closeRecordModal = () => {
    stopRecording();
    resetRecording();
    setRecordModalOpen(false);
  };

  useEffect(() => {
    if (!disclosure?.bodyHtml) return;
    const bodyEl = document.getElementById('disclosure-body');
    if (!bodyEl) return;
    const { overlay, zoomImg } = ensureImageZoomOverlay();
    if (!overlay || !zoomImg) return;
    const handlers: Array<{ el: HTMLImageElement; fn: () => void }> = [];
    bodyEl.querySelectorAll('img').forEach((img) => {
      img.style.cursor = 'zoom-in';
      const handle = () => {
        zoomImg.src = img.src;
        overlay.classList.add('active');
      };
      img.addEventListener('click', handle);
      handlers.push({ el: img, fn: handle });
    });
    return () => {
      handlers.forEach(({ el, fn }) => el.removeEventListener('click', fn));
    };
  }, [disclosure?.bodyHtml]);

  const isRecording = recordingState === 'recording';
  const isPaused = recordingState === 'paused';
  const canSaveRecording = !!recordedBlob && recordingState !== 'recording' && recordingState !== 'paused';

  if (loading) {
    return (
      <div className="glass rounded-2xl p-6 md:p-8 text-white/80">
        <p>Loading disclosure...</p>
      </div>
    );
  }

  if (error || !disclosure) {
    return (
      <div className="glass rounded-2xl p-6 md:p-8 text-white/80">
        <p>{error || 'Disclosure not found.'}</p>
      </div>
    );
  }

  const discussionLink = `disclosure-discussion.html?id=${encodeURIComponent(disclosure.id)}`;
  const audioUrl = disclosure.hasAudio ? `${API_BASE}/disclosures/${encodeURIComponent(disclosure.id)}/audio` : null;

  return (
    <>
      <section className="glass rounded-2xl p-6 md:p-8 space-y-6">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2 text-xs uppercase tracking-[0.2em] text-white/60">
            <span className={`tag-pill ${typeClass(disclosure.type)}`} id="disclosure-type">
              {disclosure.type || 'Disclosure'}
            </span>
          <span id="disclosure-meta">{disclosure.authorName || ''}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/50 mb-2">
          <span id="disclosure-dates">
            {disclosure.writtenDate ? `Published ${formatDate(disclosure.writtenDate)}` : ''}
            {disclosure.lastEditedAtUtc && Date.parse(disclosure.lastEditedAtUtc) <= Date.now() + FUTURE_SKEW_MS
              ? ` • Last edited ${formatDate(disclosure.lastEditedAtUtc)}`
              : ''}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 mb-3">
          <span className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-3 py-1">
            Reads <span>{disclosure.readCount ?? 0}</span>
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-3 py-1">
            Agree <span>{disclosure.upvotes ?? 0}</span>
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-3 py-1">
            Disagree <span>{disclosure.downvotes ?? 0}</span>
          </span>
        </div>
        <h1 className="headline text-4xl md:text-5xl font-extrabold mb-4">{disclosure.title || ''}</h1>
      </div>
      {audioUrl ? (
        <div className="rounded-xl border border-white/15 bg-white/5 p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="uppercase text-xs tracking-[0.25em] text-white/60 mb-1">Audio</p>
              <p className="text-white/80 text-sm">Listen to this disclosure.</p>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-full border border-green-400/70 bg-green-500/20 px-4 py-2 text-xs uppercase tracking-[0.25em] text-green-200 hover:bg-green-500/30 transition"
              onClick={() => audioRef.current?.play().catch(() => {})}
            >
              Listen
            </button>
          </div>
          <audio ref={audioRef} className="w-full" controls controlsList="nodownload" preload="metadata" src={audioUrl} />
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.25em] text-white/60">
        <div className="flex items-center gap-2">
          <span>
            Score: <span className="text-white/85">{voteScore}</span>
          </span>
          <span className="text-white/50">
            ({disclosure.upvotes ?? 0} agree / {disclosure.downvotes ?? 0} disagree)
          </span>
        </div>
        <div className="flex gap-2">
          <button
            className="disclosure-vote px-3 py-1 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 transition disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={voteLoading !== null}
            onClick={() => handleVote(1)}
            aria-busy={voteLoading === 1}
          >
            {voteLoading === 1 ? 'Voting...' : 'Agree'}
          </button>
          <button
            className="disclosure-vote px-3 py-1 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 transition disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={voteLoading !== null}
            onClick={() => handleVote(-1)}
            aria-busy={voteLoading === -1}
          >
            {voteLoading === -1 ? 'Voting...' : 'Disagree'}
          </button>
        </div>
        <span className={`text-[0.75rem] text-white/70 ${voteStatus ? '' : 'hidden'}`}>{voteStatus}</span>
      </div>
      <section className="glass rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3 border border-white/10">
        <div className="text-white/80">
          <p className="uppercase text-xs tracking-[0.25em] text-white/60 mb-1">Join the discussion</p>
          <p className="text-sm text-white/80">Record your reply while you listen. Pause both and regroup as needed.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={discussionLink}
            className="relative inline-flex items-center gap-2 rounded-full border border-accent/50 bg-accent/10 hover:bg-accent/20 transition px-5 py-2 text-xs uppercase tracking-[0.25em] text-accent"
          >
            View Discussion
            <span className="btn-badge">{disclosure.discussionCount ?? 0}</span>
          </a>
          <button
            type="button"
            onClick={openRecordModal}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 transition px-5 py-2 text-xs uppercase tracking-[0.25em]"
          >
            Record Reply
          </button>
        </div>
      </section>
      <article id="disclosure-body" className="prose prose-invert max-w-none text-white/85 leading-relaxed space-y-4" dangerouslySetInnerHTML={{ __html: disclosure.bodyHtml || '' }} />
      <section>
        <h2 className="headline text-2xl font-extrabold mb-3">Supporting Links</h2>
        <ul className="list-disc pl-6 space-y-2 text-white/85 break-words">
          {Array.isArray(disclosure.supportingLinks) && disclosure.supportingLinks.length ? (
            disclosure.supportingLinks.map((link) => (
              <li key={link} className="break-words">
                <a
                  className="underline underline-offset-2 break-all"
                  style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link}
                </a>
              </li>
            ))
      ) : (
        <li className="text-white/60">No supporting links provided.</li>
      )}
    </ul>
  </section>
  <div className="flex justify-center py-6">
    <img
      src={MAIN_LOGO_IMAGE_URL}
      alt="Unbought Press"
      style={{ width: 128, height: 128, objectFit: 'contain' }}
      loading="lazy"
    />
  </div>
  <section className="glass rounded-2xl p-6 flex flex-wrap items-center justify-between gap-3 border border-white/10">
    <div className="text-white/80">
      <p className="uppercase text-xs tracking-[0.25em] text-white/60 mb-1">Discussion Points</p>
          <p className="text-lg font-semibold">
            <span>{disclosure.discussionCount ?? 0}</span> statements
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={discussionLink}
            className="relative inline-flex items-center gap-2 rounded-full border border-accent/50 bg-accent/10 hover:bg-accent/20 transition px-5 py-2 text-xs uppercase tracking-[0.25em] text-accent"
          >
            View Discussion
            <span className="btn-badge">{disclosure.discussionCount ?? 0}</span>
          </a>
          <button
            type="button"
            onClick={openRecordModal}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 transition px-5 py-2 text-xs uppercase tracking-[0.25em]"
          >
            Record Reply
          </button>
        </div>
      </section>
      <div className="text-xs uppercase tracking-[0.2em] text-white/50">
        {readStatus === 'confirmed' ? 'Read recorded for this device.' : `Read will be counted after 5 minutes of active viewing.`}
      </div>
      </section>
      {recordModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-xl glass rounded-2xl border border-white/10 bg-black/80 p-6 space-y-4">
            <button
              type="button"
              onClick={closeRecordModal}
              className="absolute right-3 top-3 text-white/60 hover:text-white"
              aria-label="Close recording dialog"
            >
              ×
            </button>
            <div className="space-y-2">
              <p className="uppercase text-xs tracking-[0.25em] text-white/60">Record an audio reply</p>
              <h3 className="text-xl font-semibold text-white">Up to 3 minutes. Saves with this disclosure.</h3>
              <p className="text-white/70 text-sm">
                Record your reply while you listen. Pause both and regroup as needed. When you save, you&apos;ll finish the submission form with your audio attached.
              </p>
            </div>
            <div className="flex items-center justify-between gap-3 text-xs uppercase tracking-[0.25em] text-white/60">
              <span>{formatTimer(recordSeconds)}</span>
              <span className="text-white/60">{recordingState === 'recording' ? 'Recording' : recordingState === 'paused' ? 'Paused' : recordingState === 'stopped' ? 'Ready to save' : 'Idle'}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={startRecording}
                className="rounded-full border border-white/20 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.25em] hover:bg-white/10 disabled:opacity-60"
                disabled={isRecording}
              >
                {isRecording ? 'Recording...' : 'Record'}
              </button>
              <button
                type="button"
                onClick={pauseRecording}
                className="rounded-full border border-white/20 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.25em] hover:bg-white/10 disabled:opacity-60"
                disabled={!isRecording}
              >
                Pause
              </button>
              <button
                type="button"
                onClick={resumeRecording}
                className="rounded-full border border-white/20 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.25em] hover:bg-white/10 disabled:opacity-60"
                disabled={!isPaused}
              >
                Resume
              </button>
              <button
                type="button"
                onClick={stopRecording}
                className="rounded-full border border-white/20 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.25em] hover:bg-white/10 disabled:opacity-60"
                disabled={!isRecording && !isPaused}
              >
                Stop
              </button>
              <button
                type="button"
                onClick={resetRecording}
                className="rounded-full border border-white/20 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.25em] hover:bg-white/10"
              >
                Reset
              </button>
            </div>
            <div className="space-y-3">
              <audio
                className={`w-full ${recordPreviewUrl ? '' : 'hidden'}`}
                controls
                controlsList="nodownload"
                preload="metadata"
                src={recordPreviewUrl || undefined}
              />
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={saveRecording}
                  disabled={!canSaveRecording}
                  className="rounded-full border border-accent/50 bg-accent/10 hover:bg-accent/20 transition px-5 py-2 text-xs uppercase tracking-[0.25em] text-accent disabled:opacity-50"
                >
                  Save &amp; Continue
                </button>
                <button
                  type="button"
                  onClick={closeRecordModal}
                  className="rounded-full border border-white/20 bg-white/5 hover:bg-white/10 transition px-5 py-2 text-xs uppercase tracking-[0.25em]"
                >
                  Cancel
                </button>
                <span className="text-xs uppercase tracking-[0.2em] text-white/60">{recordingStatus}</span>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

function mountDisclosureApp() {
  const rootEl = document.getElementById('disclosure-app');
  const disclosureId = getQueryParam('id');
  if (rootEl && disclosureId) {
    const root = createRoot(rootEl);
    root.render(<DisclosureApp disclosureId={disclosureId} />);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  mountDisclosureApp();
});
